import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchPlayers } from "@/store/playersSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as teamsApi from "@api/teams.api";
import * as matchesApi from "@api/matches.api";
import type { Player } from "@/types";
import { TeamId, MIN_PLAYERS_FOR_TEAM_GENERATION } from "@/constants/teams";
import { PAGE_CONTAINER_SX } from "@/constants/layout";

interface TeamAssignment {
  playerId: string;
  teamId: number;
}

export default function Teams() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { items: players, loading } = useAppSelector((s) => s.players);
  const { notify } = useNotification();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignments, setAssignments] = useState<TeamAssignment[] | null>(null);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    dispatch(fetchPlayers());
  }, [dispatch]);

  const togglePlayer = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setAssignments(null);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await teamsApi.generateTeams(Array.from(selected));
      setAssignments(result);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleStartMatch = async () => {
    if (!assignments) return;
    setCreating(true);
    try {
      const match = await matchesApi.createMatch(assignments);
      notify(t("teams.matchCreated"), "success");
      navigate(`/matches/${match.id}/live`);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setCreating(false);
    }
  };

  const getPlayer = (id: string): Player | undefined =>
    players.find((p) => p.id === id);

  const redTeam = assignments?.filter((a) => a.teamId === TeamId.Red) ?? [];
  const blueTeam = assignments?.filter((a) => a.teamId === TeamId.Blue) ?? [];

  return (
    <Box sx={PAGE_CONTAINER_SX}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("teams.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t("teams.subtitle")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* Player Selection */}
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("teams.selectPlayers")} ({selected.size})
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List dense>
              {players.map((player) => (
                <ListItem key={player.id} disablePadding>
                  <ListItemButton onClick={() => togglePlayer(player.id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selected.has(player.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={player.name}
                      secondary={`DEF ${player.defense} | AGI ${player.agility} | TEC ${player.technique}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
          <Button
            variant="contained"
            startIcon={<ShuffleIcon />}
            onClick={handleGenerate}
            disabled={
              selected.size < MIN_PLAYERS_FOR_TEAM_GENERATION || generating
            }
            fullWidth
            sx={{ mt: 2 }}
          >
            {generating ? t("common.loading") : t("teams.generateTeams")}
          </Button>
        </Paper>

        {/* Generated Teams */}
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("teams.generatedTeams")}
          </Typography>

          {!assignments ? (
            <Typography
              sx={{
                color: "text.secondary",
                py: 4,
                textAlign: "center",
              }}
            >
              {t("teams.selectAndGenerate")}
            </Typography>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Chip label={t("teams.redTeam")} color="error" sx={{ mb: 1 }} />
                <List dense>
                  {redTeam.map((a) => {
                    const p = getPlayer(a.playerId);
                    return (
                      <ListItem key={a.playerId}>
                        <ListItemText primary={p?.name ?? "Unknown"} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ mb: 2 }}>
                <Chip
                  label={t("teams.blueTeam")}
                  color="primary"
                  sx={{ mb: 1 }}
                />
                <List dense>
                  {blueTeam.map((a) => {
                    const p = getPlayer(a.playerId);
                    return (
                      <ListItem key={a.playerId}>
                        <ListItemText primary={p?.name ?? "Unknown"} />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ShuffleIcon />}
                  onClick={handleGenerate}
                  disabled={generating}
                  fullWidth
                >
                  {t("teams.reshuffle")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartMatch}
                  disabled={creating}
                  fullWidth
                >
                  {t("teams.startMatch")}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
